import { INotificationMessage } from "@fl/shared/src/types/messages";
import { CondominioAttributes } from "@fl/shared/src/types/models/superlogica/condominios";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { InferType } from "yup";
import { cadastroCondominioComplementarSchema } from "../../../../schemas/condominios";
import AlterMessages from "../../../alerts/indes";

interface IProps {
  showModal: boolean;
  data: CondominioAttributes | null;
  onResultCadastro: (item: any) => void;
}

export type CadastroCondominioFormInputs = InferType<
  typeof cadastroCondominioComplementarSchema
>;

const diasSemana = [
  { label: "Domingo", value: 1 },
  { label: "Segunda", value: 2 },
  { label: "Terça", value: 3 },
  { label: "Quarta", value: 4 },
  { label: "Quinta", value: 5 },
  { label: "Sexta", value: 6 },
  { label: "Sábado", value: 7 },
];

const CadastroCondominioModal: React.FC<IProps> = ({
  showModal,
  data,
  onResultCadastro,
}) => {
  const [messages, setMessages] = useState<INotificationMessage[]>([]);
  const [malotesSelecionados, setMalotesSelecionados] = useState<number[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CadastroCondominioFormInputs>({
    resolver: yupResolver(cadastroCondominioComplementarSchema),
  });

  const toggleMalote = (value: number) => {
    const atualizados = malotesSelecionados.includes(value)
      ? malotesSelecionados.filter((v) => v !== value)
      : [...malotesSelecionados, value];

    setMalotesSelecionados(atualizados);
    setValue("malotes", atualizados.sort().join(";"));
  };

  useEffect(() => {
    if (data) {
      reset({ ...data });

      const dias = data.malotes
        ?.split(";")
        .map((d) => parseInt(d))
        .filter((d) => !isNaN(d));
      setMalotesSelecionados(dias || []);
    } else {
      reset({});
      setMalotesSelecionados([]);
    }

    setMessages([]);
  }, [data, showModal]);

  const onSubmit = handleSubmit((formData) => {
    onResultCadastro(formData);
  });

  return (
    <Modal
      show={showModal}
      onHide={() => onResultCadastro(null)}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {!data
            ? "Cadastro Complementar"
            : `Complementar - ${data.st_nome_cond}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row className="mt-2">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Dias de Malote</Form.Label>
                <div className="d-flex flex-wrap gap-3">
                  {diasSemana.map((dia) => {
                    const checkboxId = `malote-dia-${dia.value}`;
                    return (
                      <Form.Check
                        key={dia.value}
                        type="checkbox"
                        id={checkboxId}
                        label={dia.label}
                        checked={malotesSelecionados.includes(dia.value)}
                        onChange={() => toggleMalote(dia.value)}
                      />
                    );
                  })}
                </div>
                <Form.Control
                  type="hidden"
                  {...register("malotes")}
                  isInvalid={!!errors.malotes}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.malotes?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Data de Início</Form.Label>
                <Form.Control
                  type="date"
                  {...register("data_inicio")}
                  isInvalid={!!errors.data_inicio}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.data_inicio?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Login Web</Form.Label>
                <Form.Control
                  type="text"
                  {...register("login_web")}
                  isInvalid={!!errors.login_web}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.login_web?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Senha Web</Form.Label>
                <Form.Control
                  type="text"
                  {...register("senha_web")}
                  isInvalid={!!errors.senha_web}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.senha_web?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Cartório</Form.Label>
                <Form.Control
                  type="text"
                  {...register("cartorio")}
                  isInvalid={!!errors.cartorio}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.cartorio?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Prazo Edital</Form.Label>
                <Form.Control
                  type="number"
                  {...register("prazo_edital")}
                  isInvalid={!!errors.prazo_edital}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.prazo_edital?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6} className="d-flex align-items-center">
              <Form.Group className="mb-0 mt-4">
                <Form.Check
                  type="checkbox"
                  label="Dias úteis?"
                  {...register("prazo_edital_dias_uteis")}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col>
              <Form.Group>
                <Form.Label>Observações</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  {...register("obs")}
                  isInvalid={!!errors.obs}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.obs?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Form>

        <AlterMessages
          messages={messages}
          setMessages={setMessages}
          floating={false}
        />
      </Modal.Body>
      <Modal.Footer>
        <div className="w-100 d-flex justify-content-between">
          <Button variant="danger" onClick={() => onResultCadastro(null)}>
            Cancelar
          </Button>
          <Button variant="success" onClick={onSubmit}>
            Confirmar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CadastroCondominioModal;
